import { AxiosError } from 'axios'

import type { ApiErrorDetail, ApiErrorResponseData } from '@/types'
import { getApiErrorData } from '@/utils/api/errors/errorGuards'
import getGeneralErrorMessage from '@/utils/api/errors/getGeneralErrorMessage'

export interface ErrorInfo {
  message: string
  details: ApiErrorDetail[]
  traceId?: string
}

const NETWORK_ERROR_MESSAGE =
  'Ошибка доступа к внешнему сервису. Обратитесь к администратору.'
const TIMEOUT_ERROR_MESSAGE =
  'Превышено время ожидания ответа от сервера. Попробуйте еще раз.'
const CONNECTION_ERROR_MESSAGE =
  'Не удалось установить соединение с сервером. Проверьте подключение к интернету.'
const EMPTY_DETAIL_MESSAGE = 'Описание отсутствует'

const getNoResponseMessage = (error: AxiosError) => {
  if (error.message.includes('Network Error') || error.code === 'ERR_NETWORK') {
    return NETWORK_ERROR_MESSAGE
  }
  if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
    return TIMEOUT_ERROR_MESSAGE
  }
  return CONNECTION_ERROR_MESSAGE
}

const getResponseMessage = (
  data: ApiErrorResponseData,
  fallbackMessage: string
) => {
  if (data.message) return data.message
  if (data.detail) {
    return data.title ? `${data.title}: ${data.detail}` : data.detail
  }
  return data.title || fallbackMessage
}

export const getErrorInfo = (error: unknown): ErrorInfo => {
  const fallbackMessage = getGeneralErrorMessage(error)

  if (!(error instanceof AxiosError)) {
    return { message: fallbackMessage, details: [] }
  }
  if (!error.response) {
    return { message: getNoResponseMessage(error), details: [] }
  }

  const data = getApiErrorData(error)
  if (!data) return { message: fallbackMessage, details: [] }

  return {
    message: getResponseMessage(data, fallbackMessage),
    details: Array.isArray(data.details) ? data.details : [],
    traceId: data.traceId,
  }
}

export const formatErrorDetails = (details: ApiErrorDetail[]) =>
  details
    .map((detail) => {
      const message = detail.message || EMPTY_DETAIL_MESSAGE
      return detail.field ? `${message} (поле: ${detail.field})` : message
    })
    .join('\n')
